import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import api from '../api';

// Create the authentication context
const AuthContext = createContext();

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// AuthProvider component
export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [brokerReconnectRequired, setBrokerReconnectRequired] = useState(false);
  const [lastFailedRequest, setLastFailedRequest] = useState(null);

  // --- WRAP ALL FUNCTIONS IN useCallback ---
  
  // Refresh user data from backend
  const refreshUser = useCallback(async () => {
    // LOG 1: Confirm this function is being called.
    // console.log("--- INSIDE refreshUser() ---");

    try {
      // LOG 2: See that the API call is about to be made.
      // console.log("Fetching latest user data from /api/v1/users/me...");

      const response = await api.get('/api/v1/users/me');

      // LOG 3: Log the data BEFORE and AFTER setting the state to see the change.
      // console.log("NEW user data received from API:", response.data);

      setUser(response.data);

      // LOG 4: Confirm that the state has been updated.
      // console.log("User state in context has been updated.");

      return { success: true, data: response.data };
    } catch (error) {
      // LOG 5: Log any errors that occur during the refresh.
      console.error("--- ERROR in refreshUser() ---", error);

      console.error('Failed to refresh user data:', error);
      return { success: false, error: 'Failed to refresh user data' };
    }
  }, []); // Remove user dependency to prevent circular dependency

  // --- NEW FUNCTION: Centralized Payment Verification and Refresh ---
  const verifyPaymentAndRefresh = useCallback(async (verificationData) => {
    // console.log("🔄 Starting verifyPaymentAndRefresh with data:", verificationData);
    
    try {
      // Step 1: Verify payment with backend
      // console.log("📡 Verifying payment with backend...");
      await api.post('/api/v1/payments/verify', verificationData);
      // console.log("✅ Payment verification successful");

      // Step 2: Refresh user data to get updated subscription
      // console.log("🔄 Refreshing user data after payment verification...");
      const refreshResult = await refreshUser();
      
      if (refreshResult.success) {
        // console.log("✅ User data refreshed successfully after payment");
        // console.log("📊 Updated user state:", refreshResult.data);
        
        // Add a small delay to ensure state propagation
        await new Promise(resolve => setTimeout(resolve, 100));
        
        return { success: true, data: refreshResult.data };
      } else {
        console.error("❌ Failed to refresh user data after payment verification");
        return { success: false, error: refreshResult.error };
      }
    } catch (error) {
      console.error("❌ Payment verification failed:", error);
      
      // Handle different types of error responses
      let errorMessage = 'Payment verification failed. Please try again.';
      
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (Array.isArray(error.response.data)) {
          // Handle validation errors array
          errorMessage = error.response.data.map(err => err.msg || err.message).join(', ');
        }
      }
      
      return { success: false, error: errorMessage };
    }
  }, [refreshUser]); // Include refreshUser in dependencies

  // Check for existing token on app load
  useEffect(() => {
    const initializeAuth = async () => {
      // LOG 6: Check if a token is found on initial load.
      const storedToken = localStorage.getItem('token');

      // console.log("Initializing Auth... Token found in localStorage:", storedToken ? 'Yes' : 'No');

      if (storedToken) {
        try {
          // Verify token is still valid by making a request to get user info
          const response = await api.get('/api/v1/users/me');

          // LOG 7: Confirm successful user hydration from token.
          // console.log("Token is valid. Hydrating user state:", response.data);

          setUser(response.data);
          setToken(storedToken);
          setIsAuthenticated(true);
        } catch (error) {
          // LOG 8: Log if the token was found but is invalid.
          console.warn("Found a token in localStorage, but it's invalid or expired. Clearing auth state.");

          // Token is invalid, clear it
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          setToken(null);
          setUser(null);
          setIsAuthenticated(false);
        }
      }
      setLoading(false);
    };

    initializeAuth();
  }, []);

  // Login function
  const login = useCallback(async (email, password) => {
    try {
      // The backend expects form data for OAuth2 login
      const formData = new URLSearchParams();
      formData.append('username', email); // OAuth2 uses 'username' field for email
      formData.append('password', password);
      
      const response = await api.post('/api/v1/users/login', formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      const { access_token, refresh_token } = response.data;
      
      // Store tokens in localStorage
      localStorage.setItem('token', access_token);
      if (refresh_token) {
        localStorage.setItem('refreshToken', refresh_token);
      }
      
      // Update token state
      setToken(access_token);
      
      // Fetch user data using the token
      try {
        const userResponse = await api.get('/api/v1/users/me');
        setUser(userResponse.data);
        setIsAuthenticated(true);
      } catch (userError) {
        // If we can't fetch user data, still consider login successful
        // but clear the token as it might be invalid
        localStorage.removeItem('token');
        setToken(null);
        return { success: false, error: 'Failed to fetch user data. Please try again.' };
      }
      
      return { success: true };
    } catch (error) {
      // Handle different types of error responses
      let errorMessage = 'Login failed. Please try again.';
      
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (Array.isArray(error.response.data)) {
          // Handle validation errors array
          errorMessage = error.response.data.map(err => err.msg || err.message).join(', ');
        }
      }
      
      return { success: false, error: errorMessage };
    }
  }, []);

  // Signup function
  const signup = useCallback(async (userData) => {
    try {
      // console.log('Attempting to signup with data:', userData);
      const response = await api.post('/api/v1/users/register', userData);
      // console.log('Signup response:', response.data);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Signup error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      // Handle different types of error responses
      let errorMessage = 'Registration failed. Please try again.';
      
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (Array.isArray(error.response.data)) {
          // Handle validation errors array
          errorMessage = error.response.data.map(err => err.msg || err.message).join(', ');
        }
      }
      
      return { success: false, error: errorMessage };
    }
  }, []);

  // Logout function
  const logout = useCallback(() => {
    // Clear tokens from localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    
    // Clear state
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  // Forgot password function
  const forgotPassword = useCallback(async (email) => {
    try {
      const response = await api.post('/api/v1/password/forgot-password', { email });
      return { success: true, data: response.data };
    } catch (error) {
      // Handle different types of error responses
      let errorMessage = 'Password reset request failed. Please try again.';
      
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (Array.isArray(error.response.data)) {
          // Handle validation errors array
          errorMessage = error.response.data.map(err => err.msg || err.message).join(', ');
        }
      }
      
      return { success: false, error: errorMessage };
    }
  }, []);

  // Reset password function
  const resetPassword = useCallback(async (token, newPassword) => {
    try {
      const response = await api.post('/api/v1/password/reset-password', {
        token,
        new_password: newPassword,
      });
      return { success: true, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Password reset failed. Please try again.';
      return { success: false, error: errorMessage };
    }
  }, []);

  const updateUser = useCallback(async (profileData) => {
    try {
      const response = await api.put('/api/v1/users/me', profileData);
      
      // Update the user object in context with the new data
      setUser(response.data);
      
      return { success: true, data: response.data };
    } catch (error) {
      let errorMessage = 'Failed to update profile. Please try again.';
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (Array.isArray(error.response.data)) {
          errorMessage = error.response.data.map(err => err.msg || err.message).join(', ');
        }
      }
      return { success: false, error: errorMessage };
    }
  }, []);

  // Delete account function
  const deleteAccount = useCallback(async () => {
    try {
      const response = await api.delete('/api/v1/users/me');
      
      // If successful, logout the user immediately
      logout();
      
      return { success: true, data: response.data };
    } catch (error) {
      let errorMessage = 'Failed to delete account. Please try again.';
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (Array.isArray(error.response.data)) {
          errorMessage = error.response.data.map(err => err.msg || err.message).join(', ');
        }
      }
      return { success: false, error: errorMessage };
    }
  }, [logout]); // Include logout in dependencies since it calls logout

  // Broker reconnect handlers
  const triggerBrokerReconnect = useCallback((failedRequest = null) => {
    setBrokerReconnectRequired(true);
    setLastFailedRequest(failedRequest);
  }, []);

  const clearBrokerReconnect = useCallback(() => {
    setBrokerReconnectRequired(false);
    setLastFailedRequest(null);
  }, []);

  const retryFailedRequest = useCallback(async () => {
    if (!lastFailedRequest) return null;
    
    try {
      // Retry the original request
      const response = await api.request(lastFailedRequest);
      clearBrokerReconnect();
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Failed to retry request:', error);
      return { success: false, error: error.response?.data?.detail || 'Request failed' };
    }
  }, [lastFailedRequest, clearBrokerReconnect]);

  // Listen for broker reconnect events from axios interceptor
  useEffect(() => {
    const handleBrokerReconnectEvent = (event) => {
      const { broker, error, failedRequest } = event.detail;
      console.log('Broker reconnect required:', broker, error);
      triggerBrokerReconnect(failedRequest);
    };

    window.addEventListener('broker-reconnect-required', handleBrokerReconnectEvent);
    
    return () => {
      window.removeEventListener('broker-reconnect-required', handleBrokerReconnectEvent);
    };
  }, [triggerBrokerReconnect]);

  // --- THIS IS THE CRITICAL FIX ---
  // Memoize the context value to ensure it updates when dependencies change
  const value = useMemo(() => {
    // Calculate hasActiveSubscription inside useMemo to ensure it's reactive
    // More robust calculation with better error handling
    let hasActiveSubscription = false;
    
    if (user?.subscription_plan && user?.subscription_valid_until) {
      try {
        const validUntilDate = new Date(user.subscription_valid_until);
        const currentDate = new Date();
        hasActiveSubscription = validUntilDate > currentDate;
        
        // console.log("📊 Subscription calculation details:", {
        //   subscription_plan: user.subscription_plan,
        //   subscription_valid_until: user.subscription_valid_until,
        //   validUntilDate: validUntilDate.toISOString(),
        //   currentDate: currentDate.toISOString(),
        //   hasActiveSubscription
        // });
      } catch (error) {
        console.error("❌ Error calculating hasActiveSubscription:", error);
        hasActiveSubscription = false;
      }
    }

    // console.log("🔄 AuthContext value recalculated:", {
    //   user: user?.email,
    //   subscription_plan: user?.subscription_plan,
    //   subscription_valid_until: user?.subscription_valid_until,
    //   hasActiveSubscription
    // });

    return {
      isAuthenticated,
      user,
      token,
      loading,
      hasActiveSubscription, // This value is now reactive to changes in `user`
      login,
      signup,
      logout,
      forgotPassword,
      resetPassword,
      updateUser,
      deleteAccount,
      refreshUser,
      verifyPaymentAndRefresh, // Expose the new centralized payment function
      brokerReconnectRequired,
      triggerBrokerReconnect,
      clearBrokerReconnect,
      retryFailedRequest,
    };
  }, [ // --- THIS ARRAY MUST BE COMPLETE ---
    isAuthenticated, 
    user, 
    token, 
    loading, 
    login, 
    signup, 
    logout, 
    refreshUser,
    forgotPassword,
    resetPassword,
    updateUser,
    deleteAccount,
    verifyPaymentAndRefresh, // Include the new function in dependencies
    brokerReconnectRequired,
    triggerBrokerReconnect,
    clearBrokerReconnect,
    retryFailedRequest,
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
